import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { requestJsonData } from '~/utils/request-json-data';
import type { Feed } from '~/services/layout';
import { resolvePluginDataSource } from '~/services/pipes/plugin-data-source.ts';

type LoadingState = 'idle' | 'loading';

export type UseClientFeedOptions<TData> = {
  initialData?: TData | null;
  uiComponentId?: string;
};

type FeedState<TData> = {
  state: LoadingState;
  data: TData | null | undefined;
  stableData: TData | null | undefined;
};

function getFeedEntryCount(data: unknown): number {
  if (!data || typeof data !== 'object' || !('entry' in data)) return 0;

  const entry: unknown = (data as { entry?: unknown }).entry;

  return Array.isArray(entry) ? entry.length : 0;
}

/**
 * Custom hook that fetches client feed data using requestJsonData instead of Remix fetcher.
 * This prevents errors from bubbling up to the ErrorBoundary.
 */
export const useClientFeed = <TData = Feed>(
  clientFeedApiEndpoint: string | null,
  { initialData, uiComponentId }: UseClientFeedOptions<TData> = {}
) => {
  const [state, setState] = useState<LoadingState>('idle');
  const [data, setData] = useState(initialData);
  const previousDataRef = useRef<typeof initialData>(undefined);
  const isLoadingRef = useRef<boolean>(false);
  const currentFetchEndpointRef = useRef<string | null>(null);
  const pendingRefetchRef = useRef<boolean>(false);
  const pendingRefetchForceFreshRef = useRef<boolean>(false);

  // Sync initialData into local state/ref when it changes; preserve stable data on endpoint change.
  useEffect(() => {
    if (initialData === undefined) {
      setData(undefined);
      previousDataRef.current = undefined;
      return;
    }

    if (initialData === null) {
      return;
    }

    setData(initialData);

    const initialFeedEntryCount: number = getFeedEntryCount(initialData);

    if (initialFeedEntryCount > 0) {
      previousDataRef.current = initialData;
    }
  }, [clientFeedApiEndpoint, initialData]);

  // uiComponentId change is expected to bring a new clientFeedApiEndpoint; reset loading state only.
  useEffect(() => {
    setState('idle');
    isLoadingRef.current = false;

    // Reset the fetch endpoint tracker
    currentFetchEndpointRef.current = null;
  }, [uiComponentId]);

  // Update stableData when data changes
  useEffect(() => {
    if (data) {
      previousDataRef.current = data;
    }
  }, [data]);

  const fetchData = useCallback(
    async (forceFresh: boolean = false) => {
      if (!clientFeedApiEndpoint) {
        return;
      }

      if (isLoadingRef.current) {
        pendingRefetchRef.current = true;
        pendingRefetchForceFreshRef.current =
          pendingRefetchForceFreshRef.current || forceFresh;
        return;
      }

      // Track the endpoint for this fetch to prevent stale responses
      const fetchEndpoint = clientFeedApiEndpoint;
      currentFetchEndpointRef.current = fetchEndpoint;
      isLoadingRef.current = true;
      setState('loading');

      const pluginDataSourceHandler = resolvePluginDataSource(
        clientFeedApiEndpoint
      );

      const responseData = pluginDataSourceHandler
        ? (await pluginDataSourceHandler(clientFeedApiEndpoint)).data
        : await (async () => {
            let url = clientFeedApiEndpoint;

            if (forceFresh) {
              const urlObj = new URL(
                clientFeedApiEndpoint,
                window.location.origin
              );
              urlObj.searchParams.set('force-fresh', 'true');
              url = urlObj.pathname + urlObj.search;
            }

            return requestJsonData({ url });
          })();

      // Only update state if this fetch is still for the current endpoint
      if (currentFetchEndpointRef.current !== fetchEndpoint) {
        return;
      }

      // requestJsonData returns undefined on error, so check if we got data
      if (responseData) {
        const nextEntryCount: number = getFeedEntryCount(responseData);
        const currentEntryCount: number = getFeedEntryCount(
          data ?? previousDataRef.current
        );

        if (pluginDataSourceHandler) {
          setData(responseData);
        } else if (
          forceFresh ||
          nextEntryCount > 0 ||
          currentEntryCount === 0
        ) {
          setData(responseData);
        }
      }

      setState('idle');
      isLoadingRef.current = false;

      if (pendingRefetchRef.current) {
        const shouldForceFresh = pendingRefetchForceFreshRef.current;
        pendingRefetchRef.current = false;
        pendingRefetchForceFreshRef.current = false;

        void fetchData(shouldForceFresh);
      }
    },
    [clientFeedApiEndpoint]
  );

  const feedState: FeedState<TData> = useMemo(
    () => ({
      state,
      data,
      stableData: data || previousDataRef.current,
    }),
    [state, data]
  );

  return useMemo(() => ({ feedState, fetchData }), [feedState, fetchData]);
};
